import React, { useState } from 'react';
import {
  Search,
  Bell,
  Heart,
  MessageSquare,
  FileText,
  CheckCircle,
  ChevronDown,
  LucideIcon
} from 'lucide-react';

// Use LucideIcon type for better type safety
interface Step {
  id: number;
  title: string;
  description: string;
  Icon: LucideIcon; // Use the component itself, not a rendered ReactNode
}

// Map the data to use the component for easier styling later
const stepsData: Step[] = [
  {
    id: 1,
    title: 'Search & Filter',
    description:
      'Browse verified listings and use powerful filters to find your perfect home based on location, price, and amenities.',
    Icon: Search
  },
  {
    id: 2,
    title: 'Set Alerts',
    description:
      'Create personalized search alerts to get instant notifications via email or push notification for new properties matching your criteria.',
    Icon: Bell
  },
  {
    id: 3,
    title: 'Save Favorites',
    description:
      'Easily bookmark potential properties, organize them into lists, and compare features side-by-side to make an informed choice.',
    Icon: Heart
  },
  {
    id: 4,
    title: 'Contact & View',
    description:
      'Directly message landlords or agents through the platform, ask detailed questions, and securely schedule property viewings.',
    Icon: MessageSquare
  },
  {
    id: 5,
    title: 'Apply',
    description:
      'Submit your rental application digitally. Upload necessary documents securely for a faster, smoother, and completely paperless process.',
    Icon: FileText
  },
  {
    id: 6,
    title: 'Move In',
    description:
      'Review and sign the lease agreement online, securely pay your deposit, and receive your keys! Welcome to your new home.',
    Icon: CheckCircle
  }
];

const HowItWorksAccordion: React.FC = () => {
  const [openStep, setOpenStep] = useState<number | null>(1);

  const toggleStep = (id: number) => {
    setOpenStep(openStep === id ? null : id);
  };

  return (
    <div className="mt-4 bg-white py-10 px-4 sm:px-6 lg:px-8 rounded-2xl shadow-xl border border-gray-100">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <header className="text-center mb-10">
          <h2 className="text-4xl font-extrabold text-gray-900 leading-tight mb-3">
            Your Home Journey
          </h2>
          <p className="text-lg text-indigo-600 font-medium">
            Discover the path to your perfect home in 6 simple, guided steps.
          </p>
        </header>

        {/* Accordion Container with Timeline Effect */}
        <div className="relative space-y-4">
          {/* Vertical Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-indigo-200 hidden md:block" />

          {stepsData.map((step, index) => {
            const isOpen = openStep === step.id;
            const isLast = index === stepsData.length - 1;

            // Determine colors for the active/inactive state
            const primaryColor = isOpen ? 'text-indigo-600' : 'text-gray-500';
            const bgColor = isOpen
              ? 'bg-indigo-50'
              : 'bg-white hover:bg-gray-50';
            const ringColor = isOpen ? 'ring-2 ring-indigo-400' : '';

            return (
              <div
                key={step.id}
                className={`relative pl-14 md:pl-20 pr-4 py-4 rounded-xl transition-all duration-300 ${bgColor} ${ringColor} shadow-sm`}
              >
                {/* --- Visual Timeline Point & Step Number --- */}
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 hidden md:block">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 border-4 ${
                      isOpen
                        ? 'bg-indigo-600 text-white border-indigo-100 shadow-lg'
                        : 'bg-white text-gray-600 border-indigo-200'
                    }`}
                  >
                    {index + 1}
                  </div>
                </div>

                {/* --- Mobile/Small Screen Step Number & Icon (Positioned to the left of content) --- */}
                <div className="absolute left-0 top-0 mt-4 ml-4 flex items-center gap-2 md:hidden">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-indigo-500 text-white flex-shrink-0">
                    {index + 1}
                  </div>
                  <step.Icon className={`w-5 h-5 ${primaryColor}`} />
                </div>

                {/* Accordion Header */}
                <button
                  onClick={() => toggleStep(step.id)}
                  className="w-full flex items-center justify-between text-left focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Icon (Only visible on larger screens to the right of the number) */}
                    <div className="hidden md:block">
                      <step.Icon
                        className={`w-6 h-6 ${primaryColor} transition-colors duration-300`}
                      />
                    </div>

                    {/* Title */}
                    <h3
                      className={`text-base md:text-xl font-semibold transition-colors duration-300 ${
                        isOpen ? 'text-indigo-700' : 'text-gray-800'
                      } md:pt-0 pt-7`}
                    >
                      {step.title}
                    </h3>
                  </div>

                  {/* Chevron */}
                  <ChevronDown
                    className={`flex-shrink-0 w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      isOpen ? 'transform rotate-180 text-indigo-600' : ''
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? 'grid-rows-[1fr] opacity-100 pt-3'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksAccordion;
